import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { Icon, Checkbox, SearchBox, TooltipHost } from "@fluentui/react";
import { t } from "i18next";
import themeContext from "../../../../context/theme/ThemeContext";
import ModalMain from "../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../components/Modal/ModalFooter";
import { getLibraryCatalog, loadLibraryCatalog, subscribeLibraryCatalog } from "./libraryTagStore";
import { getLibraryStudyTagsApi } from "../../../../service/StudyListService";
import "./libraryTag.css";

const UNCATEGORIZED_ID = "uncategorized";
const nameTipStyles = { root: { display: "block", minWidth: 0, flex: 1 } };

const NameTip = ({ id, name, className }) => {
  const full = name == null ? "" : String(name);
  return (
    <TooltipHost content={full} id={id} styles={nameTipStyles}>
      <span className={className}>{full}</span>
    </TooltipHost>
  );
};

const stateOf = (tagIds, selected) => {
  const n = tagIds.length;
  const nin = tagIds.filter((id) => selected.includes(id)).length;
  return { n, nin, all: n > 0 && nin === n, mixed: nin > 0 && nin < n };
};

const checkStyles = {
  root: { margin: 0, pointerEvents: "none" },
  label: { margin: 0, padding: 0, alignItems: "center" },
  checkbox: { margin: 0 },
  text: { display: "none" },
};

const Check = ({ all, mixed, disabled }) => (
  <Checkbox checked={!!all} indeterminate={!!mixed} disabled={!!disabled} styles={checkStyles} />
);

const ApplyLibraryModal = ({ show, studyId, onDismiss, onSave, skipLabel }) => {
  const { themeName, getThemeColor, getScrollBarStyleForTheme } = useContext(themeContext);
  const [catalog, setCatalog] = useState(getLibraryCatalog);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [focusId, setFocusId] = useState(null);
  const openedTagIds = useRef([]);
  const scrollClass = getScrollBarStyleForTheme(themeName, "common-modal-scroll");

  useEffect(() => subscribeLibraryCatalog(setCatalog), []);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setQuery("");
    setFocusId(null);
    setSelected([]);
    openedTagIds.current = [];
    loadLibraryCatalog();
    getLibraryStudyTagsApi(studyId).then((response) => {
      if (cancelled || !response || !response.data || response.data.result != "SUCCESS") return;
      const ids = (response.data.data && response.data.data.tagIds) || [];
      openedTagIds.current = ids;
      setSelected(ids);
    });
    return () => {
      cancelled = true;
    };
  }, [show, studyId]);

  const uncategorizedName = t("study-list.library-tag.uncategorized");
  const groups = useMemo(() => {
    const used = new Set();
    catalog.categories.forEach((category) => category.tagIds.forEach((id) => used.add(id)));
    const real = [...catalog.categories]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((category) => ({ id: category.id, name: category.name, tagIds: category.tagIds, virtual: false }));
    return [
      {
        id: UNCATEGORIZED_ID,
        name: uncategorizedName,
        tagIds: catalog.tags.filter((tag) => !used.has(tag.id)).map((tag) => tag.id),
        virtual: true,
      },
      ...real,
    ];
  }, [catalog, uncategorizedName]);

  const tagById = useMemo(() => {
    const map = new Map();
    catalog.tags.forEach((tag) => map.set(tag.id, tag));
    return map;
  }, [catalog.tags]);

  const categoryNamesFor = (tagId, exceptId) =>
    groups
      .filter((group) => !group.virtual && group.id !== exceptId && group.tagIds.includes(tagId))
      .map((group) => group.name);

  const q = query.trim().toLowerCase();
  const visible = groups
    .map((group) => {
      const catMatch = q !== "" && group.name.toLowerCase().includes(q);
      const visibleTagIds = q === "" || catMatch
        ? group.tagIds
        : group.tagIds.filter((id) => tagById.get(id)?.name.toLowerCase().includes(q));
      return { ...group, visibleTagIds, catMatch };
    })
    .filter((group) => q === "" || group.catMatch || group.visibleTagIds.length > 0);

  const focus = visible.find((group) => group.id === focusId && group.tagIds.length > 0)
    || visible.find((group) => group.tagIds.length > 0)
    || visible[0]
    || null;

  const toggleGroup = (group) => {
    if (!group || group.tagIds.length === 0) return;
    setSelected((prev) => {
      const current = stateOf(group.tagIds, prev);
      if (current.all) return prev.filter((id) => !group.tagIds.includes(id));
      return [...prev, ...group.tagIds.filter((id) => !prev.includes(id))];
    });
  };

  const toggleTag = (tagId) => {
    setSelected((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const saveLabel = t("study-list.library-tag.save-tags");
  const sameIds = (left, right) => {
    const a = [...left].map(String).sort();
    const b = [...right].map(String).sort();
    return a.length === b.length && a.every((id, index) => id === b[index]);
  };
  const selectionChanged = !sameIds(openedTagIds.current, selected);

  const focusTags = (focus?.visibleTagIds || [])
    .map((id) => tagById.get(id))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
  const focusState = focus ? stateOf(focus.tagIds, selected) : null;

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading={t("study-list.library-tag.add-tags-title")}
        handleCancel={onDismiss}
      />
    ),
    body: (
      <div
        className={`global-modal-body library-apply ${scrollClass}`}
        data-theme={themeName}
        style={{ color: getThemeColor("primary-color") }}
      >
        <SearchBox
          className="library-apply-search"
          value={query}
          placeholder={t("study-list.library-tag.find-category-or-tag")}
          ariaLabel={t("study-list.library-tag.find-category-or-tag")}
          clearButtonProps={{ ariaLabel: t("button.clear") }}
          onChange={(_, value) => setQuery(value || "")}
          onClear={() => setQuery("")}
          styles={{ root: { width: 280, maxWidth: "100%", height: 32 } }}
        />
        <div className="library-apply-panes">
          <div className="library-apply-cats">
            <div className="library-filter-menu-heading">{t("study-list.library-tag.categories")}</div>
            <div className={`library-apply-list ${scrollClass}`}>
              {visible.length === 0 ? (
                <div className="library-filter-empty">{t("study-list.library-tag.no-tag-match", { query: query.trim() })}</div>
              ) : (
                visible.map((group) => {
                  const empty = group.tagIds.length === 0;
                  const counts = stateOf(group.tagIds, selected);
                  const active = focus && focus.id === group.id;
                  return (
                    <div
                      key={group.id}
                      className={`library-apply-cat${active ? " is-active" : ""}${empty ? " is-empty" : ""}${group.virtual ? " is-virtual" : ""}`}
                      onClick={() => setFocusId(group.id)}
                    >
                      {empty ? (
                        <button type="button" className="library-filter-cat-check" disabled>
                          <Check all={false} mixed={false} disabled />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="library-filter-cat-check"
                          aria-label={t("study-list.library-tag.select-all-tags")}
                          onClick={(event) => {
                            event.stopPropagation();
                            setFocusId(group.id);
                            toggleGroup(group);
                          }}
                        >
                          <Check all={counts.all} mixed={counts.mixed} />
                        </button>
                      )}
                      <NameTip id={`library-apply-cat-${group.id}`} name={group.name} className="library-filter-cat-name" />
                      <span className="library-filter-cat-side">
                        {empty
                          ? t("study-list.library-tag.no-tags")
                          : t("study-list.library-tag.selected-count", { selected: counts.nin, total: counts.n })}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="library-apply-tags">
            {focus ? (
              <>
                <div className="library-apply-focus">
                  <NameTip id={`library-apply-focus-${focus.id}`} name={focus.name} className="library-apply-focus-name" />
                  <span className="library-filter-muted">
                    {focus.virtual
                      ? t("study-list.library-tag.not-in-category")
                      : focus.tagIds.length === 1
                        ? t("study-list.library-tag.one-tag")
                        : t("study-list.library-tag.tag-count", { count: focus.tagIds.length })}
                  </span>
                </div>
                {focus.tagIds.length > 0 ? (
                  <button type="button" className="library-filter-select-all" onClick={() => toggleGroup(focus)}>
                    <Check all={focusState.all} mixed={focusState.mixed} />
                    {t("study-list.library-tag.select-all-tags")}
                  </button>
                ) : null}
                <div className={`library-apply-tags-list ${scrollClass}`}>
                  {focusTags.length === 0 ? (
                    <div className="library-filter-empty">{t("study-list.library-tag.no-tags")}</div>
                  ) : (
                    focusTags.map((tag) => {
                      const also = categoryNamesFor(tag.id, focus.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          className="library-apply-tag"
                          onClick={() => toggleTag(tag.id)}
                        >
                          <Check all={selected.includes(tag.id)} mixed={false} />
                          <span className="library-apply-tag-copy">
                            <NameTip id={`library-apply-tag-${tag.id}`} name={tag.name} className="library-apply-tag-name" />
                            {also.length > 0 ? (
                              <span className="library-filter-muted">
                                {t("study-list.library-tag.also-in", { names: also.join(", ") })}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <div className="library-filter-empty">{t("study-list.library-tag.no-tags")}</div>
            )}
          </div>
        </div>
        <p className="library-apply-note">{t("study-list.library-tag.category-applies-tags")}</p>
        {selected.length > 0 ? (
          <div className="library-apply-tray">
            {selected
              .map((id) => tagById.get(id))
              .filter(Boolean)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((tag) => (
                <button key={tag.id} type="button" className="library-apply-chip" onClick={() => toggleTag(tag.id)}>
                  {tag.name}
                  <Icon iconName="Cancel" />
                </button>
              ))}
          </div>
        ) : null}
      </div>
    ),
    footer: (
      <ModalFooter
        DefaultButtonList={[{ title: skipLabel ? t("study-list.library-tag.skip") : t("button.cancel"), onclick: onDismiss }]}
        PrimaryButtonList={[{ title: saveLabel, onclick: () => onSave(selected), disabled: catalog.tags.length === 0 || !selectionChanged }]}
      />
    ),
    width: "960px",
    maxWidth: "calc(100vw - 48px)",
  };

  if (!show) return null;

  return <ModalMain updatedModal={true} showModal={show} modalDetails={modalDetails} />;
};

export default ApplyLibraryModal;
